// Catalog.ID Client — Recovery Kit Preset + Sheet Fields
// ============================================================================
// The shared kit-setup control: a distribution-pattern picker (Addendum §9.4)
// plus one row per sheet — its purpose and the number of
// ADDITIONAL printed copies. Used by both registration wizards and by the
// Settings / organization kit creator, so a kit reissued later is set up
// exactly like one created at registration.
//
// The preset fixes k and n and PREFILLS purposes and additional copies; both
// stay editable. The master document set holds every sheet once; additional
// copies of one sheet type carry the identical share and count as ONE sheet
// toward the threshold — they buy availability, never a lower quorum.

import { createElement } from '@catalog-shell';
import { cardTypeLetter } from './booklet/shared';
import { MAX_CARD_EXTRA_COPIES } from '../crypto/recovery-kit';
import {
    defaultPresetFor,
    presetById,
    presetsFor,
    type RecoveryKitPreset,
    type RecoveryKitPresetKind,
} from './recovery-kit-presets';

export interface RecoveryKitCardFieldsOptions {
    kind: RecoveryKitPresetKind;
    /** Restore a previous choice (wizard resume); falls back to the default. */
    presetId?: string;
    /** Restore edited purposes / additional copies, index-aligned with the sheets. */
    labels?: string[];
    extraCopies?: number[];
    /** Distinguishes the control's element ids when a page holds two. */
    idPrefix?: string;
    /**
     * The registration wizards' kit is not optional, and its pattern is the
     * member's choice: nothing is preselected and no sheet shows until one is
     * picked (staging test 2026-10-15). A restored `presetId` still counts as
     * chosen. Settings' creator leaves this off and keeps its default.
     */
    requirePresetChoice?: boolean;
    /**
     * The fewest additional copies a sheet may have. The wizards ask for 1: the
     * sheets in the printed set stay with it, and only COPIES are handed to the
     * people and places the pattern names — with none, there would be nothing
     * to hand over. Prefills below it are raised to it. Default 0.
     */
    minExtraCopies?: number;
    /** The pattern was chosen or changed. */
    onPresetChange?: (preset: RecoveryKitPreset) => void;
}

export interface RecoveryKitCardFieldsValue {
    preset: RecoveryKitPreset;
    labels: string[];
    extraCopies: number[];
}

export interface RecoveryKitCardFields {
    element: HTMLElement;
    /** Has a distribution pattern been chosen (always, without `requirePresetChoice`)? */
    hasChoice(): boolean;
    /** The current picker state, ready for `createRecoveryKit`. Throws while no
     *  pattern is chosen — check {@link hasChoice} first. */
    read(): RecoveryKitCardFieldsValue;
}

export function createRecoveryKitCardFields(options: RecoveryKitCardFieldsOptions): RecoveryKitCardFields {
    const presets = presetsFor(options.kind);
    const minCopies = Math.max(0, Math.min(MAX_CARD_EXTRA_COPIES, options.minExtraCopies ?? 0));
    const restored = presetById(options.presetId);
    let selected: RecoveryKitPreset | null = restored && presets.includes(restored)
        ? restored
        : options.requirePresetChoice ? null : defaultPresetFor(options.kind);

    // Restored values apply to the restored preset only — switching presets
    // refills every row from the new pattern.
    let restoredLabels: string[] | undefined = options.labels;
    let restoredExtraCopies: number[] | undefined = options.extraCopies;

    const container = createElement('div', { className: 'cid-kit-fields' });
    const prefix = options.idPrefix ?? 'kit';

    const presetField = createElement('div', { className: 'cid-auth-field' });
    const presetLabel = createElement('label', {
        className: 'cid-key-field-label',
        textContent: 'Distribution pattern:',
    });
    presetLabel.setAttribute('for', `${prefix}-preset`);
    const select = createElement('select', { className: 'cid-auth-input' });
    select.id = `${prefix}-preset`;
    if (selected === null) {
        // Nothing preselected: a prompt in the box, never one of the patterns.
        const prompt = createElement('option', { textContent: 'Choose a distribution pattern…' });
        prompt.value = '';
        prompt.selected = true;
        prompt.disabled = true;
        select.appendChild(prompt);
    }
    for (const preset of presets) {
        const option = createElement('option', { textContent: preset.title });
        option.value = preset.id;
        option.selected = preset.id === selected?.id;
        select.appendChild(option);
    }
    presetField.appendChild(presetLabel);
    presetField.appendChild(select);
    container.appendChild(presetField);

    const presetNote = createElement('p', { className: 'cid-pane-note' });
    container.appendChild(presetNote);

    const rowsWrap = createElement('div');
    container.appendChild(rowsWrap);

    const labelInputs: HTMLInputElement[] = [];
    const extraCopiesInputs: HTMLInputElement[] = [];

    function renderRows(): void {
        rowsWrap.innerHTML = '';
        labelInputs.length = 0;
        extraCopiesInputs.length = 0;
        if (selected === null) {
            presetNote.textContent = '';
            return;
        }
        const preset = selected;
        presetNote.textContent = preset.description;

        rowsWrap.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: minCopies > 0
                ? 'Each sheet\'s purpose (who or where it is meant for — printed on the sheet, not secret) '
                    + 'and how many ADDITIONAL copies of it to print. The sheets in your document set stay '
                    + 'with it; you hand out only the additional copies, so every sheet has at least '
                    + `${minCopies === 1 ? 'one' : minCopies}. Copies carry the same share and count as one `
                    + 'sheet toward the threshold.'
                : 'Each sheet\'s purpose (who or where it is meant for — printed on the sheet, not '
                    + 'secret) and how many ADDITIONAL copies of it to print. Your document set always holds '
                    + 'every sheet once; additional copies are printed after it, carry the same share, and '
                    + 'count as one sheet toward the threshold.',
        }));

        preset.cards.forEach((card, i) => {
            const row = createElement('div', { className: 'cid-kit-card-row' });
            row.appendChild(createElement('span', {
                className: 'cid-key-field-label cid-kit-card-letter',
                textContent: `Sheet ${cardTypeLetter(i + 1)}:`,
            }));

            // Caption matches the printed sheet's row (review 2026-09-02, third
            // round): the field is the sheet's PURPOSE, not a free-form label.
            row.appendChild(createElement('span', {
                className: 'cid-key-field-label',
                textContent: 'Purpose:',
            }));
            const labelInput = createElement('input', { className: 'cid-auth-input cid-kit-card-label' });
            labelInput.type = 'text';
            labelInput.maxLength = 32;
            labelInput.value = restoredLabels?.[i] ?? card.label;
            labelInput.placeholder = `Sheet ${cardTypeLetter(i + 1)} purpose`;
            row.appendChild(labelInput);
            labelInputs.push(labelInput);

            row.appendChild(createElement('span', {
                className: 'cid-key-field-label',
                textContent: 'Additional copies:',
            }));
            const extraCopiesInput = createElement('input', { className: 'cid-auth-input cid-kit-card-copies' });
            extraCopiesInput.type = 'number';
            extraCopiesInput.min = String(minCopies);
            extraCopiesInput.max = String(MAX_CARD_EXTRA_COPIES);
            extraCopiesInput.step = '1';
            extraCopiesInput.value = String(Math.max(minCopies, restoredExtraCopies?.[i] ?? card.extraCopies));
            row.appendChild(extraCopiesInput);
            extraCopiesInputs.push(extraCopiesInput);

            rowsWrap.appendChild(row);
        });
    }

    select.addEventListener('change', () => {
        const picked = presetById(select.value);
        if (!picked) return;
        selected = picked;
        // A new pattern means new roles: the previous edits no longer map.
        restoredLabels = undefined;
        restoredExtraCopies = undefined;
        renderRows();
        options.onPresetChange?.(picked);
    });

    renderRows();

    return {
        element: container,
        hasChoice: () => selected !== null,
        read(): RecoveryKitCardFieldsValue {
            if (selected === null) throw new Error('No distribution pattern has been chosen');
            return {
                preset: selected,
                labels: labelInputs.map(i => i.value.trim()),
                extraCopies: extraCopiesInputs.map(i => clampExtraCopies(i.value, minCopies)),
            };
        },
    };
}

/** Number inputs hand back strings, and the field can be emptied or typed into. */
function clampExtraCopies(raw: string, min: number): number {
    const n = Math.round(Number(raw));
    if (!Number.isFinite(n)) return min;
    return Math.min(MAX_CARD_EXTRA_COPIES, Math.max(min, n));
}
